/**
 * excelUtils.js
 * Utilitaires partagés pour l'export Excel CBConnect.
 *
 * - Suffixe de nom de fichier avec date/heure + signature
 * - Mise en forme : largeurs colonnes, entêtes, merges
 */
import { XLSX } from '../../../utils/xlsxShim';

import i18n from '../../../i18n';

// ── Nom de fichier avec date/heure + signature ────────────────────
export function nomFichier(base) {
  const now = new Date();
  const date = now.toLocaleDateString('fr-FR', {
    year: 'numeric', month: '2-digit', day: '2-digit'
  }).replace(/\//g, '-');                          // "21-04-2026"
  const heure = now.toLocaleTimeString('fr-FR', {
    hour: '2-digit', minute: '2-digit', second: '2-digit'
  }).replace(/:/g, 'h').replace(' ', '');          // "14h32h08"
  return `${base}_${date}_${heure}_by_CBConnect.xlsx`;
}

// ── Largeurs colonnes ─────────────────────────────────────────────
export function setColWidths(ws, widths) {
  ws['!cols'] = widths.map(w => ({ wch: w }));
}

// ── Hauteurs lignes ───────────────────────────────────────────────
export function setRowHeights(ws, heights) {
  ws['!rows'] = heights.map(h => ({ hpt: h }));
}

// ── Fusionner des cellules ────────────────────────────────────────
export function mergeCells(ws, merges) {
  // merges : [{s:{r,c}, e:{r,c}}, ...]  (0-indexed)
  ws['!merges'] = (ws['!merges'] || []).concat(merges);
}

// ── En-tête standardisé BNCB ─────────────────────────────────────
/**
 * Insère 3 lignes d'en-tête au début d'une feuille AoA.
 * Retourne le tableau enrichi + les merges à appliquer.
 */
export function ajouterEntete(aoa, titre, sousTitre, moisAnnee, nbCols) {
  const entete = [
    ['BNCB-TOGO', ...Array(nbCols - 2).fill(null), i18n.t('statistiques:reporting.excel.genereParCbconnect')],
    [titre, ...Array(nbCols - 1).fill(null)],
    [sousTitre + (moisAnnee ? `  -  ${moisAnnee}` : ''), ...Array(nbCols - 1).fill(null)],
    [],  // ligne vide
  ];
  return {
    aoa: [...entete, ...aoa],
    offset: entete.length,  // décalage pour les merges des données
    merges: [
      { s: { r: 1, c: 0 }, e: { r: 1, c: nbCols - 1 } },  // titre sur toute la largeur
      { s: { r: 2, c: 0 }, e: { r: 2, c: nbCols - 1 } },  // sous-titre idem
    ],
  };
}

// ── Construire et écrire un workbook ─────────────────────────────
/**
 * @param sheets  [{ name, aoa, colWidths, rowHeights, merges }]
 * @param nomBase nom sans date ni extension
 */
export async function ecrireExcel(sheets, nomBase) {
  const wb = XLSX.utils.book_new();
  for (const { name, aoa, colWidths, rowHeights, merges } of sheets) {
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    if (colWidths) setColWidths(ws, colWidths);
    if (rowHeights) setRowHeights(ws, rowHeights);
    if (merges) mergeCells(ws, merges);
    XLSX.utils.book_append_sheet(wb, ws, name);
  }
  await XLSX.writeFile(wb, nomFichier(nomBase));
}

// ── Helper : construire un AoA pour tableau financier (nb+mt) ────
/**
 * Génère le tableau AoA standard pour un reporting nb/montant.
 *
 * @param label    titre du bloc ("TABLEAU I - ...")
 * @param lignes   tableau de lignes (parPays ou parCompagnie)
 * @param total    ligne totale
 * @param getLabel fn pour extraire le label de chaque ligne
 * @param moisL    libellé mois en majuscules
 * @param annee    année N
 * @param anneeRef année N-1
 */
export function tableauFinancierAoa(label, lignes, total, getLabel,
  moisL, annee, anneeRef) {
  const n = annee, n1 = anneeRef;
  const tx = (cle, options) => i18n.t(`statistiques:reporting.excel.${cle}`, options);
  const rows = [
    [`=== ${label} ===`],
    ['',
      tx('moisDe', { mois: moisL }), '', '', '', '', '', '', '',
      tx('janvierA', { mois: moisL }), '', '', '', '', '', '', '',
      tx('finAnneeAnnee', { annee: n }), ''],
    [tx('payeursMembres'),
      n1, '', n, '', tx('varNb'), tx('varMt'), tx('pctNb'), tx('pctMt'),
      n1, '', n, '', tx('varNb'), tx('varMt'), tx('pctNb'), tx('pctMt'), tx('nbMajuscules'), tx('mtFcfa')],
    ['', tx('nb'), tx('mt'), tx('nb'), tx('mt'), '', '', '', '', tx('nb'), tx('mt'), tx('nb'), tx('mt'), '', '', '', '', '', ''],
  ];

  const toRow = (l) => {
    const lbl = getLabel(l);
    const vMoisNb = l.nb_mois_n1 ? (l.nb_mois_n - l.nb_mois_n1) / l.nb_mois_n1 : '';
    const vMoisMt = Number(l.mt_mois_n1) ? (Number(l.mt_mois_n) - Number(l.mt_mois_n1)) / Number(l.mt_mois_n1) : '';
    const vCumulNb = l.nb_cumul_n1 ? (l.nb_cumul_n - l.nb_cumul_n1) / l.nb_cumul_n1 : '';
    const vCumulMt = Number(l.mt_cumul_n1) ? (Number(l.mt_cumul_n) - Number(l.mt_cumul_n1)) / Number(l.mt_cumul_n1) : '';
    return [lbl,
      l.nb_mois_n1, Number(l.mt_mois_n1), l.nb_mois_n, Number(l.mt_mois_n),
      vMoisNb, vMoisMt, '', '',
      l.nb_cumul_n1, Number(l.mt_cumul_n1), l.nb_cumul_n, Number(l.mt_cumul_n),
      vCumulNb, vCumulMt, '', '',
      l.nb_fda_n, Number(l.mt_fda_n)];
  };

  lignes.forEach(l => rows.push(toRow(l)));
  const t = total;
  rows.push(['TOTAL',
    t.nb_mois_n1, Number(t.mt_mois_n1), t.nb_mois_n, Number(t.mt_mois_n), '', '', '', '',
    t.nb_cumul_n1, Number(t.mt_cumul_n1), t.nb_cumul_n, Number(t.mt_cumul_n), '', '', '', '',
    t.nb_fda_n, Number(t.mt_fda_n)]);
  rows.push([]);
  return rows;
}

// ── Largeurs colonnes standard pour tableau financier ────────────
export const COL_WIDTHS_FINANCIER = [
  18,  // label
  7, 14, 7, 14, 8, 10, 7, 7,   // MOIS
  7, 14, 7, 14, 8, 10, 7, 7,   // CUMUL
  7, 14,                        // FIN D'ANNÉE
];
